import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dataService } from '../../../services/dataService';
import { Student } from '../../../data/types';
import Button from '../../ui/Button';
import Avatar from '../../ui/Avatar';
import Dropdown from '../../ui/Dropdown';

const StudentLogin: React.FC = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState<string | null>(null);
  // Dashboard version selection with v4 as default
  const [selectedDashboard, setSelectedDashboard] = useState<string>('v4');
  // PIN requirement removed as per requirements
  
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const allStudents = await dataService.getStudents();
        setStudents(allStudents);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching students:', error);
        setLoading(false);
      }
    };
    
    fetchStudents();
  }, []);
  
  const handleStudentSelect = (studentId: string) => {
    // Direct login without PIN
    setSelectedStudent(studentId);
    localStorage.setItem('activeStudentId', studentId);
    
    if (selectedDashboard === 'v4') {
      navigate('/student/dashboardv4');
    } else if (selectedDashboard === 'v3') {
      navigate('/student/dashboardv3');
    } else if (selectedDashboard === 'v2') {
      navigate('/student/dashboardv2'); 
    } else {
      navigate('/student/dashboardv1'); // Updated to use explicit v1 route
    }
  };
  
  // PIN entry removed as per requirements
  
  // PIN entry removed as per requirements
  
  // PIN submission removed as per requirements
  
  // Back to student selection removed as PIN screen is no longer used
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-purple-50 flex justify-center items-center">
        <div className="animate-bounce text-4xl text-purple-600">
          Loading...
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-purple-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-md py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <div className="flex items-center justify-center">
  <img src="/assets/images/swunmath_logo.png" alt="SwunMath Logo" className="h-12 w-auto" />
</div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="flex-grow flex items-center justify-center p-4">
        <div className="max-w-4xl w-full">
          {/* Student Selection Screen */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-blue-500 p-6 text-white">
              <h1 className="text-3xl font-bold text-center text-white">Welcome to SwunMath!</h1>
<p className="text-center mt-2 opacity-90">Please select the dashboard style and then select a student to start the student dashboard</p>
            </div>

            {/* Dashboard Version Selector */}
              <div className="px-6 pt-6 pb-2 flex justify-end">
                <div className="flex items-center">
                  <label htmlFor="dashboard-version" className="mr-2 text-sm font-medium text-gray-700">
                    Dashboard:
                  </label>
                  <Dropdown
                    id="dashboard-version"
                    value={selectedDashboard}
                    onChange={(e) => setSelectedDashboard(e.target.value)}
                    options={[
                      { value: 'v4', label: 'Game style 2 (Default)' },
                      { value: 'v3', label: 'Game style 1' }
                    ]}
                    className="w-48"
                  />
                </div>
              </div>
              
              <div className="p-6 pt-2">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {students.map(student => (
                    <button
                      key={student.id}
                      onClick={() => handleStudentSelect(student.id)}
                      className={`flex flex-col items-center p-4 rounded-xl transition-all ${
                        selectedStudent === student.id
                          ? 'bg-purple-100 border-2 border-purple-500'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                      }`}
                    >
                      <Avatar 
                        src={student.avatarUrl || `https://placehold.co/100x100/6366f1/ffffff?text=${student.name.charAt(0)}`} 
                        alt={student.name} 
                        size="lg" 
                        className="mb-3" 
                      />
                      <h3 className="font-bold text-gray-800 text-center">{student.name}</h3>
                      <p className="text-sm text-gray-500">Grade {student.grade}</p>
                    </button>
                  ))}
                </div>
                
                <div className="mt-8 flex justify-center">
                  <Button 
                    variant="primary" 
                    onClick={() => navigate('/')}
                    className="rounded-xl"
                  >
                    <span className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                      </svg>
                      Back to Main
                    </span>
                  </Button>
                </div>
              </div>
            </div>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="bg-white py-4 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-gray-500">
            © 2025 SwunMath Learning Platform. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default StudentLogin;
